import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import type {
  ITopFiveApiV2EndpointsStaffUpdateStaffUpdateStaffRequest,
  ITopFiveApiV2EndpointsStaffUpdateStaffUpdateStaffResponse,
} from '@/api/models';
import {
  useITopFiveApiV2EndpointsStaffUpdateStaffUpdateStaffEndpoint,
  getITopFiveApiV2EndpointsStaffGetStaffListGetStaffListEndpointQueryKey,
  getITopFiveApiV2EndpointsStaffGetStaffByIdGetStaffByIdEndpointQueryKey,
} from '@/api/api.generated';
import { parseValidationErrors } from './useAddStaff';

type UpdateStaffApiRequest = ITopFiveApiV2EndpointsStaffUpdateStaffUpdateStaffRequest & {
  staffId: number;
};
type UpdateStaffApiResponse = ITopFiveApiV2EndpointsStaffUpdateStaffUpdateStaffResponse;

interface ApiValidationError {
  errors?: Record<string, string[]>;
}

/**
 * Response type for useUpdateStaff hook
 */
export interface UseUpdateStaffResult {
  /** Mutation function to update a staff member */
  updateStaff: (request: UpdateStaffApiRequest) => Promise<UpdateStaffApiResponse>;
  /** Whether the mutation is in progress */
  isPending: boolean;
  /** Whether the mutation was successful */
  isSuccess: boolean;
  /** Whether there was an error */
  isError: boolean;
  /** Error object if any */
  error: AxiosError<ApiValidationError> | null;
  /** Parsed validation errors for form fields */
  validationErrors: Record<string, string>;
  /** Reset the mutation state */
  reset: () => void;
}

/**
 * Custom hook to update an existing staff member
 *
 * Uses React Query mutation for API call management.
 * Automatically invalidates the staff list and detail caches on success.
 *
 * @example
 * ```tsx
 * const { updateStaff, isPending, validationErrors } = useUpdateStaff();
 *
 * const handleSubmit = async (data) => {
 *   try {
 *     await updateStaff({ staffId: id, ...data });
 *     navigate('/staff');
 *   } catch (error) {
 *     // Validation errors are available in validationErrors
 *   }
 * };
 * ```
 */
export const useUpdateStaff = (): UseUpdateStaffResult => {
  const queryClient = useQueryClient();
  const mutation = useITopFiveApiV2EndpointsStaffUpdateStaffUpdateStaffEndpoint({
    mutation: {
      onSuccess: (_data, variables) => {
        const staffId = variables.id;
        // Invalidate the staff list query to refetch updated data
        queryClient.invalidateQueries({
          queryKey: getITopFiveApiV2EndpointsStaffGetStaffListGetStaffListEndpointQueryKey(),
        });
        // Invalidate the specific staff detail query
        queryClient.invalidateQueries({
          queryKey: getITopFiveApiV2EndpointsStaffGetStaffByIdGetStaffByIdEndpointQueryKey(staffId),
        });
      },
    },
    axios: { withCredentials: true },
  });

  const validationErrors = mutation.error
    ? parseValidationErrors(mutation.error as AxiosError<ApiValidationError>)
    : {};

  return {
    updateStaff: (request: UpdateStaffApiRequest) => {
      const { staffId, ...body } = request;
      return mutation.mutateAsync({ id: staffId, data: body }).then(response => response.data);
    },
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error as AxiosError<ApiValidationError> | null,
    validationErrors,
    reset: mutation.reset,
  };
};

export default useUpdateStaff;